import { useMemo, useState } from "react";
import { cn } from "@/lib/cn";
import { AgentCursorArrow } from "@/components/cursors/agent-cursor-arrow";
import { VoiceListeningGlass } from "@/components/cursors/voice-listening-glass";
import {
  IndicatorPreview,
  TintShowcaseCard,
} from "./voice-indicator-tint-demos";

const agents = [
  { name: "Denker", color: "#30D158" },
  { name: "Claude", color: "#8B5CF6" },
  { name: "Codex", color: "#38BDF8" },
];

export function VoiceIndicatorPage() {
  const [volume, setVolume] = useState(0.45);
  const [agentIndex, setAgentIndex] = useState(0);
  const activeAgent = agents[agentIndex] ?? agents[0]!;
  const volumeLabel = useMemo(() => Math.round(volume * 100), [volume]);

  return (
    <div className="min-h-full bg-[#0A0A0B] px-8 py-10 text-white">
      <style>{`
        @keyframes denker-voice-glass-reflect {
          0%, 100% { transform: translateX(calc(var(--voice-glass-travel, 18%) * -1)) skewX(-8deg); }
          50% { transform: translateX(var(--voice-glass-travel, 18%)) skewX(8deg); }
        }
      `}</style>
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#30D158]">
            Desktop Voice
          </p>
          <h1 className="mt-2 text-2xl font-semibold">Voice Indicator</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/55">
            Preview of the native-glass listening bubble shown while voice input
            is recording. The reflected halo is iridescent while listening,
            green while processing, and red when input fails. Higher volume
            makes the internal reflection move faster and farther, not just more
            saturated. Runtime size is 104 x 34px for Denker + mic and 140 x
            34px when camera capture is active.
          </p>
        </div>

        <section className="mb-8 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-medium text-white/85">
                Live Recording Preview
              </h2>
              <p className="mt-1 text-xs text-white/42">
                Adjust volume to tune the reflection intensity inside the glass
                bubble.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {agents.map((agent, index) => (
                <button
                  key={agent.name}
                  type="button"
                  data-testid={`voice-agent-${agent.name.toLowerCase()}`}
                  onClick={() => setAgentIndex(index)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                    index === agentIndex
                      ? "border-white/25 bg-white/12 text-white"
                      : "border-white/10 bg-white/[0.03] text-white/45 hover:bg-white/[0.07]",
                  )}
                >
                  {agent.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="relative flex h-72 items-center justify-center overflow-hidden rounded-xl border border-black/10 bg-[radial-gradient(circle_at_34%_12%,rgba(56,189,248,0.2),transparent_30%),radial-gradient(circle_at_68%_36%,rgba(244,114,182,0.18),transparent_34%),linear-gradient(135deg,#f8fafc,#e9edf4)]">
              <div className="absolute inset-0 opacity-[0.26] [background-image:radial-gradient(circle,rgba(17,24,39,0.42)_1px,transparent_1px)] [background-size:18px_18px]" />
              <div className="w-[420px] max-w-[82%]">
                <VoiceHeroFrame color={activeAgent.color} volume={volume} />
              </div>
            </div>

            <div className="rounded-xl border border-white/[0.06] bg-black/20 p-4">
              <label
                htmlFor="voice-volume-preview"
                className="mb-3 block text-xs font-medium text-white/55"
              >
                Volume {volumeLabel}%
              </label>
              <input
                id="voice-volume-preview"
                data-testid="voice-volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(event) => setVolume(Number(event.target.value))}
                className="w-full accent-[#30D158]"
              />
              <div className="mt-6 space-y-3">
                {[
                  { label: "Silent", value: 0 },
                  { label: "Speaking", value: 0.45 },
                  { label: "Peak", value: 0.82 },
                ].map((sample) => (
                  <div
                    key={sample.label}
                    className="flex items-center justify-between gap-3"
                  >
                    <IndicatorPreview
                      color={activeAgent.color}
                      volume={sample.value}
                    />
                    <span className="text-[10px] font-medium text-white/35">
                      {sample.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mb-8 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="mb-5">
            <h2 className="text-sm font-medium text-white/85">
              Theme Behavior
            </h2>
            <p className="mt-1 text-xs text-white/42">
              Same listening bubble tuned for dark and light.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <ThemeCard label="Dark" theme="dark" color={activeAgent.color} />
            <ThemeCard label="Light" theme="light" color={activeAgent.color} />
          </div>
        </section>

        <section className="mb-8 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="mb-5">
            <h2 className="text-sm font-medium text-white/85">
              Native Glass State Check
            </h2>
            <p className="mt-1 text-xs text-white/42">
              Compact states for inspecting the reflected halo, microphone, and
              one-bubble camera layout.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <TintShowcaseCard
              label="Listening"
              color={activeAgent.color}
              volume={volume}
            />
            <TintShowcaseCard
              label="Processing"
              color="#30D158"
              volume={0}
              processing
            />
            <TintShowcaseCard
              label="Error"
              color="#30D158"
              volume={0}
              deadMic
            />
          </div>
        </section>

        <section className="mb-8 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="mb-5">
            <h2 className="text-sm font-medium text-white/85">
              Light Background Check
            </h2>
            <p className="mt-1 text-xs text-white/42">
              Same indicator over a bright surface to inspect shadow weight.
            </p>
          </div>

          <div className="relative h-56 overflow-hidden rounded-xl border border-black/10 bg-[#F7F7F4]">
            <div className="absolute inset-x-0 top-0 h-16 bg-white" />
            <div className="absolute left-8 top-7 h-9 w-36 rounded-md border border-black/10 bg-white shadow-sm" />
            <div className="absolute right-8 top-7 h-9 w-24 rounded-md border border-black/10 bg-[#ECEDE8]" />
            <div className="absolute inset-x-8 bottom-8 grid grid-cols-3 gap-4">
              <div className="h-16 rounded-md border border-black/10 bg-white" />
              <div className="h-16 rounded-md border border-black/10 bg-[#E9F2EA]" />
              <div className="h-16 rounded-md border border-black/10 bg-white" />
            </div>
            <div className="absolute left-1/2 top-1/2 w-[360px] max-w-[70%] -translate-x-1/2 -translate-y-1/2">
              <IndicatorPreview
                color={activeAgent.color}
                volume={volume}
                camera
                surface="light"
              />
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
            <div className="mb-4 text-xs text-white/45">Recording + camera</div>
            <IndicatorPreview color="#30D158" volume={0.7} camera />
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
            <div className="mb-4 text-xs text-white/45">Processing</div>
            <IndicatorPreview color="#30D158" volume={0} processing />
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
            <div className="mb-4 text-xs text-white/45">No input detected</div>
            <IndicatorPreview color="#30D158" volume={0} deadMic />
          </div>
        </section>
      </div>
    </div>
  );
}

function ThemeCard({
  label,
  theme,
  color,
}: {
  label: string;
  theme: "dark" | "light";
  color: string;
}) {
  const isLight = theme === "light";

  return (
    <div
      className={cn(
        "relative min-h-44 overflow-hidden rounded-xl border p-4",
        isLight
          ? "border-black/10 bg-[#F8FAFC]"
          : "border-white/[0.08] bg-black/35",
      )}
    >
      <div
        className={cn(
          "mb-8 text-xs font-medium",
          isLight ? "text-black/55" : "text-white/45",
        )}
      >
        {label}
      </div>
      <div className="flex justify-center">
        <VoiceListeningGlass
          color={color}
          volume={0.56}
          camera
          agentName="Denker"
          showAgentName
          surface={isLight ? "light" : "dark"}
          theme={theme}
          testId={`voice-theme-${theme}`}
        />
      </div>
    </div>
  );
}

function VoiceHeroFrame({ color, volume }: { color: string; volume: number }) {
  return (
    <div
      className="rounded-[32px] border border-white/60 bg-white/30 p-5 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.45),inset_0_1px_0_rgba(255,255,255,0.55)]"
      data-testid="voice-cursor-hero-frame"
    >
      <div
        className="mx-auto flex h-28 w-[280px] items-start justify-center gap-2 pt-8"
        data-testid="voice-cursor-runtime-cluster"
      >
        <AgentCursorArrow
          color={color}
          mode="glass"
          shape="soft"
          liquidLevel={0.26}
          position="relative"
          tipTestId="voice-cursor-hero-agent"
        />
        <VoiceListeningGlass
          color={color}
          volume={volume}
          camera
          agentName="Denker"
          showAgentName
          surface="light"
          theme="light"
          testId="voice-hero-runtime-glass"
        />
      </div>
    </div>
  );
}
